import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AdminPortalLayout from './AdminPortalLayout';
import {
  AdminMe, fetchReferralDetail, recordReferralPayout, triggerInfluencerPasswordReset,
  deleteReferralPayout,
} from '../lib/admin-portal-api';
import {
  FaArrowLeft, FaCheckCircle, FaExclamationTriangle, FaDollarSign, FaUniversity,
} from '../lib/icons';

interface Props { me: AdminMe }

export default function AdminPortalInfluencerDetail({ me }: Props) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ref, setRef] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [payoutAmt, setPayoutAmt] = useState('');
  const [payoutNote, setPayoutNote] = useState('');
  const [payoutRef, setPayoutRef] = useState('');
  const [payoutMethod, setPayoutMethod] = useState('');
  const [payoutDate, setPayoutDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [bankAck, setBankAck] = useState(false);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const detail = await fetchReferralDetail(Number(id));
      setRef(detail);
      setPayoutMethod((m) => m || detail.payment_method || '');
    }
    catch (e: any) { setError(e?.response?.data?.error || 'Failed to load'); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (!me.isSuperAdmin) return <Navigate to="/admin-portal" replace />;

  const onPayout = async (e: React.FormEvent) => {
    e.preventDefault();
    const amt = parseFloat(payoutAmt);
    if (!Number.isFinite(amt) || amt <= 0) { setMsg({ type: 'error', text: 'Enter a positive amount.' }); return; }
    if (!bankAck) { setMsg({ type: 'error', text: 'Please confirm you sent funds to the payment profile above.' }); return; }
    setBusy(true); setMsg(null);
    try {
      await recordReferralPayout(ref.id, {
        amount: amt,
        note: payoutNote || undefined,
        reference: payoutRef || undefined,
        method: payoutMethod || undefined,
        paid_at: payoutDate ? new Date(payoutDate).toISOString() : undefined,
      });
      setPayoutAmt(''); setPayoutNote(''); setPayoutRef(''); setBankAck(false);
      setMsg({ type: 'success', text: `Recorded ${ref.commission_currency} ${amt.toFixed(2)} payout.` });
      await load();
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.error || 'Payout failed' });
    } finally { setBusy(false); }
  };

  const onDeletePayout = async (payoutId: number, amount: number) => {
    if (!window.confirm(`Remove this $${amount.toFixed(2)} payout? Pending will go back up by the same amount.`)) return;
    setBusy(true); setMsg(null);
    try {
      await deleteReferralPayout(ref.id, payoutId);
      setMsg({ type: 'success', text: 'Payout removed.' });
      await load();
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.error || 'Failed to remove' });
    } finally { setBusy(false); }
  };

  const copy = (s: string) => navigator.clipboard?.writeText(s).catch(() => {});

  const onSendPwReset = async () => {
    if (!window.confirm(`Send a password reset link to ${ref.email}?`)) return;
    setBusy(true); setMsg(null);
    try {
      await triggerInfluencerPasswordReset(ref.id);
      setMsg({ type: 'success', text: 'Password reset email sent.' });
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.error || 'Failed to send' });
    } finally { setBusy(false); }
  };

  return (
    <AdminPortalLayout me={me} active="referrals">
      <Helmet><title>{ref?.name || 'Influencer'} — EatMeat Admin</title></Helmet>

      <button onClick={() => navigate('/admin-portal/referrals')}
        className="inline-flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 mb-4">
        <FaArrowLeft /> Back to referrals
      </button>

      {loading ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center text-sm text-gray-400">Loading…</div>
      ) : error ? (
        <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
      ) : ref ? (
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Identity + stats */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <h2 className="font-bold text-gray-800 text-lg">{ref.name}</h2>
                  <p className="text-sm text-gray-500">{ref.email}</p>
                  <div className="flex flex-wrap gap-2 mt-2 text-[11px]">
                    <span className="font-mono bg-primary/10 text-primary px-2 py-0.5 rounded-full">{ref.code}</span>
                    {ref.platform && <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full uppercase">{ref.platform}</span>}
                    {ref.is_active ? (
                      <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><FaCheckCircle /> Active</span>
                    ) : (
                      <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">Inactive</span>
                    )}
                    <span className="text-gray-500">Rate {ref.commission_rate}%</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-3 mt-5">
                <Stat label="Signups" value={ref.stats.signups} color="text-blue-700" bg="from-blue-50 to-blue-100/40" />
                <Stat label="Trials" value={ref.stats.trials} color="text-amber-700" bg="from-amber-50 to-amber-100/40" />
                <Stat label="Paid" value={ref.stats.paid} color="text-purple-700" bg="from-purple-50 to-purple-100/40" />
                <Stat label="Refunds" value={ref.stats.refunds} color="text-red-700" bg="from-red-50 to-red-100/40" />
              </div>
            </div>

            {msg && (
              <div className={`px-4 py-3 rounded-xl text-sm flex items-start gap-2 ${
                msg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800'
                                       : 'bg-red-50 border border-red-200 text-red-700'
              }`}>
                {msg.type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}
                {msg.text}
              </div>
            )}

            {/* Recent events */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-gray-800 mb-3">Recent activity</h3>
              {ref.events.length === 0 ? (
                <p className="text-sm text-gray-400">No events yet.</p>
              ) : (
                <div className="space-y-2 max-h-[420px] overflow-auto">
                  {ref.events.map((e: any) => (
                    <div key={e.id} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg">
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-gray-700 capitalize">
                          {e.is_trial ? 'Trial' : e.is_first_paid ? 'Paid (first)' : Number(e.commission_amount) < 0 ? 'Refund' : e.event_type}
                          {e.user && <span className="ml-2 font-normal text-gray-400 text-[11px]">{e.user.username || e.user.email}</span>}
                        </div>
                        <div className="text-[10px] text-gray-400">{new Date(e.createdAt).toLocaleString()} · {e.provider_store || '—'}</div>
                      </div>
                      <div className="text-right text-xs">
                        {e.amount ? <div className="font-semibold text-gray-700">{e.currency} {Number(e.amount).toFixed(2)}</div> : null}
                        {e.commission_amount ? (
                          <div className={Number(e.commission_amount) < 0 ? 'text-red-600' : 'text-green-600'}>
                            {Number(e.commission_amount) > 0 ? '+' : ''}{e.currency} {Number(e.commission_amount).toFixed(2)}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Referred users */}
            <ReferredUsersPanel users={ref.users} />

          </div>

          {/* Sidebar — financial + actions */}
          <div className="space-y-4">
            {/* Commission summary */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-gray-800 mb-3 inline-flex items-center gap-2"><FaDollarSign className="text-orange-500" /> Commission</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-gray-500">Earned</dt><dd className="font-semibold text-gray-800">{ref.commission_currency} {ref.commission_earned.toFixed(2)}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Paid out</dt><dd className="font-semibold text-blue-700">{ref.commission_currency} {ref.commission_paid.toFixed(2)}</dd></div>
                <div className="flex justify-between pt-2 border-t border-gray-100"><dt className="text-gray-700 font-semibold">Pending</dt><dd className="font-bold text-orange-700">{ref.commission_currency} {ref.pending_payout.toFixed(2)}</dd></div>
              </dl>
            </div>

            {/* Payment profile — prominent, BEFORE the payout form */}
            <div className="bg-white rounded-2xl border-2 border-orange-200 shadow-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-gray-800 inline-flex items-center gap-2">
                  <FaUniversity className="text-orange-500" /> Payment profile
                </h3>
                <Link to={`${process.env.REACT_APP_STRAPI_API_URL || ''}/admin/content-manager/collectionType/api::referral.referral/${ref.id}`}
                  target="_blank"
                  className="text-[11px] font-semibold text-primary hover:underline">Edit →</Link>
              </div>
              <p className="text-[11px] text-gray-500 mb-3">Use these details to send the payout, then record it below.</p>
              <dl className="space-y-2 text-xs">
                <FieldRow label="Method" value={ref.payment_method} />
                <FieldRow label="Pay email" value={ref.payment_email} copyOnClick={() => copy(ref.payment_email || '')} />
                <FieldRow label="Country" value={ref.country} />
                <FieldRow label="Bank" value={ref.bank_name} />
                <FieldRow label="Account holder" value={ref.bank_account_holder} copyOnClick={() => copy(ref.bank_account_holder || '')} />
                <FieldRow label="Account #" value={ref.bank_account_number} copyOnClick={() => copy(ref.bank_account_number || '')} sensitive />
                <FieldRow label="Routing / SWIFT" value={ref.bank_routing_swift} copyOnClick={() => copy(ref.bank_routing_swift || '')} sensitive />
                <FieldRow label="Tax ID" value={ref.tax_id} sensitive />
              </dl>
              {!ref.payment_method && !ref.bank_account_number && !ref.payment_email && (
                <div className="mt-3 p-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
                  ⚠️ No payment details on file. Contact the influencer before paying.
                </div>
              )}
            </div>

            {/* Record payout */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-gray-800 mb-3">Record payout</h3>
              <form onSubmit={onPayout} className="space-y-2">
                <div>
                  <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Amount ({ref.commission_currency})</label>
                  <input type="number" step="0.01" value={payoutAmt} onChange={(e) => setPayoutAmt(e.target.value)}
                    placeholder={`Up to ${ref.pending_payout.toFixed(2)}`}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Method</label>
                    <select value={payoutMethod} onChange={(e) => setPayoutMethod(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none mt-1">
                      <option value="">—</option>
                      <option value="bank_transfer">Bank transfer</option>
                      <option value="paypal">PayPal</option>
                      <option value="wise">Wise</option>
                      <option value="payoneer">Payoneer</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Paid date</label>
                    <input type="date" value={payoutDate} onChange={(e) => setPayoutDate(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none mt-1" />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Reference / transaction id</label>
                  <input value={payoutRef} onChange={(e) => setPayoutRef(e.target.value)}
                    placeholder="e.g. wire confirmation #"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none mt-1" />
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Note (optional)</label>
                  <input value={payoutNote} onChange={(e) => setPayoutNote(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:border-primary focus:outline-none mt-1" />
                </div>
                <label className="flex items-start gap-2 mt-2 cursor-pointer">
                  <input type="checkbox" checked={bankAck} onChange={(e) => setBankAck(e.target.checked)} className="mt-0.5 accent-primary" />
                  <span className="text-[11px] text-gray-600">I sent the funds to the payment profile above.</span>
                </label>
                <button type="submit" disabled={busy || !bankAck}
                  className="w-full py-2.5 rounded-xl text-sm font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-50">
                  {busy ? 'Saving…' : 'Record payout'}
                </button>
                <p className="text-[10px] text-gray-400">Writes a payout audit row and bumps <code>commission_paid</code>. Does not actually transfer money.</p>
              </form>
            </div>

            {/* Account actions */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-gray-800 mb-3">Account actions</h3>
              <button onClick={onSendPwReset} disabled={busy}
                className="w-full py-2.5 rounded-xl text-sm font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 transition-colors">
                Send password reset email
              </button>
            </div>
          </div>

          {/* Payout history — full width below */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-gray-800">Payout history</h3>
                <span className="text-[11px] text-gray-400">{ref.payouts?.length || 0} record{(ref.payouts?.length || 0) === 1 ? '' : 's'}</span>
              </div>
              {(!ref.payouts || ref.payouts.length === 0) ? (
                <p className="text-sm text-gray-400 text-center py-6">No payouts recorded yet.</p>
              ) : (
                <div className="overflow-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
                        <th className="px-4 py-2.5">Paid at</th>
                        <th className="px-4 py-2.5">Amount</th>
                        <th className="px-4 py-2.5">Method</th>
                        <th className="px-4 py-2.5">Reference</th>
                        <th className="px-4 py-2.5">Note</th>
                        <th className="px-4 py-2.5">Recorded by</th>
                        <th className="px-4 py-2.5 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {ref.payouts.map((p: any) => (
                        <tr key={p.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-xs text-gray-700">{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : '—'}</td>
                          <td className="px-4 py-2 font-semibold text-gray-800">{p.currency} {Number(p.amount).toFixed(2)}</td>
                          <td className="px-4 py-2 text-xs text-gray-500">{p.method || '—'}</td>
                          <td className="px-4 py-2 text-xs font-mono text-gray-700">{p.reference || '—'}</td>
                          <td className="px-4 py-2 text-xs text-gray-500 max-w-xs truncate">{p.note || '—'}</td>
                          <td className="px-4 py-2 text-xs text-gray-400">{p.recorded_by_email || '—'}</td>
                          <td className="px-4 py-2 text-right">
                            <button onClick={() => onDeletePayout(p.id, Number(p.amount))} disabled={busy}
                              className="text-[11px] px-2 py-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100 font-semibold disabled:opacity-50">
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </AdminPortalLayout>
  );
}

/** Compact row with optional copy-on-click + sensitive masking */
function FieldRow({ label, value, copyOnClick, sensitive = false }: { label: string; value: string | null | undefined; copyOnClick?: () => void; sensitive?: boolean }) {
  const v = value && value.toString().trim() ? value.toString() : null;
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-gray-400 font-medium shrink-0">{label}</dt>
      <dd className="text-right min-w-0">
        {v ? (
          copyOnClick ? (
            <button onClick={copyOnClick} title="Copy" className="font-medium text-gray-700 break-all text-right hover:text-primary">
              {sensitive && v.length > 6 ? `${v.slice(0, 2)}…${v.slice(-4)}` : v}
            </button>
          ) : (
            <span className="font-medium text-gray-700 break-all">{v}</span>
          )
        ) : <span className="text-gray-300">—</span>}
      </dd>
    </div>
  );
}

function Stat({ label, value, color, bg }: { label: string; value: number; color: string; bg: string }) {
  return (
    <div className={`bg-gradient-to-br ${bg} ring-1 ring-gray-100 rounded-xl px-3 py-3`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`text-2xl font-extrabold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

/* ───── Referred users panel ───── */

type ReferredUser = {
  id: number;
  username: string | null;
  email: string;
  is_premium: boolean;
  subscription_status: string | null;
  createdAt: string;
  conversion: 'paid' | 'trial' | 'signup_only' | 'refunded' | 'none';
  trial_at: string | null;
  paid_at: string | null;
  total_commission: number;
  currency: string;
  refund_count: number;
};

const CONVERSION_FILTERS: { id: string; label: string }[] = [
  { id: 'all',         label: 'All' },
  { id: 'paid',        label: 'Paid' },
  { id: 'trial',       label: 'Trial' },
  { id: 'signup_only', label: 'Signup only' },
  { id: 'refunded',    label: 'Refunded' },
];

type SortKey = 'joined' | 'paid_at' | 'commission';

function ReferredUsersPanel({ users }: { users: ReferredUser[] }) {
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('joined');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const summary = useMemo(() => {
    const total = users.length;
    let paid = 0, trial = 0, signupOnly = 0, refunded = 0, totalCommission = 0;
    let currency = 'USD';
    for (const u of users) {
      if (u.conversion === 'paid') paid++;
      else if (u.conversion === 'trial') trial++;
      else if (u.conversion === 'signup_only') signupOnly++;
      else if (u.conversion === 'refunded') refunded++;
      totalCommission += u.total_commission || 0;
      if (u.currency) currency = u.currency;
    }
    return { total, paid, trial, signupOnly, refunded, totalCommission, currency };
  }, [users]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let rows = users.filter((u) => {
      if (filter !== 'all' && u.conversion !== filter) return false;
      if (needle) {
        const hay = `${u.username || ''} ${u.email || ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
    rows = [...rows].sort((a, b) => {
      let av: number, bv: number;
      if (sortKey === 'joined') {
        av = new Date(a.createdAt).getTime();
        bv = new Date(b.createdAt).getTime();
      } else if (sortKey === 'paid_at') {
        av = a.paid_at ? new Date(a.paid_at).getTime() : 0;
        bv = b.paid_at ? new Date(b.paid_at).getTime() : 0;
      } else {
        av = a.total_commission || 0;
        bv = b.total_commission || 0;
      }
      return sortDir === 'desc' ? bv - av : av - bv;
    });
    return rows;
  }, [users, filter, q, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir('desc'); }
  };
  const sortIcon = (key: SortKey) => sortKey !== key ? '' : sortDir === 'desc' ? ' ↓' : ' ↑';

  if (users.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <h3 className="font-bold text-gray-800 mb-3">Referred users</h3>
        <p className="text-sm text-gray-400">No users attributed yet.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Header strip */}
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h3 className="font-bold text-gray-800">Referred users · {summary.total}</h3>
          <div className="text-xs text-gray-500 flex flex-wrap gap-3">
            <span><span className="font-semibold text-purple-700">{summary.paid}</span> paid</span>
            <span><span className="font-semibold text-amber-700">{summary.trial}</span> trial</span>
            <span><span className="font-semibold text-gray-600">{summary.signupOnly}</span> signup</span>
            {summary.refunded > 0 && <span><span className="font-semibold text-red-700">{summary.refunded}</span> refunded</span>}
            <span className="text-gray-400">·</span>
            <span><span className="font-semibold text-green-700">{summary.currency} {summary.totalCommission.toFixed(2)}</span> earned</span>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="bg-gray-50 rounded-xl p-1 flex gap-1 overflow-x-auto">
            {CONVERSION_FILTERS.map((f) => (
              <button key={f.id} onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                  filter === f.id ? 'bg-primary text-white' : 'text-gray-600 hover:bg-white'
                }`}>
                {f.label}
              </button>
            ))}
          </div>
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search email or username…"
            className="text-sm px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none w-full sm:w-72"
          />
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="p-10 text-center text-sm text-gray-400">No users match these filters.</div>
      ) : (
        <div className="overflow-auto max-h-[480px]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 text-left text-[10px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-2.5">User</th>
                <th className="px-4 py-2.5">Conversion</th>
                <th className="px-4 py-2.5">Subscription</th>
                <th className="px-4 py-2.5 cursor-pointer hover:text-gray-800" onClick={() => toggleSort('paid_at')}>
                  Paid at{sortIcon('paid_at')}
                </th>
                <th className="px-4 py-2.5 text-right cursor-pointer hover:text-gray-800" onClick={() => toggleSort('commission')}>
                  Commission{sortIcon('commission')}
                </th>
                <th className="px-4 py-2.5 text-right cursor-pointer hover:text-gray-800" onClick={() => toggleSort('joined')}>
                  Joined{sortIcon('joined')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <Link to={`/admin-portal/users/${u.id}`} className="block">
                      <div className="text-sm font-semibold text-gray-800 truncate max-w-xs">{u.username || '—'}</div>
                      <div className="text-xs text-gray-500 truncate max-w-xs">{u.email}</div>
                    </Link>
                  </td>
                  <td className="px-4 py-3"><ConversionBadge value={u.conversion} refundCount={u.refund_count} /></td>
                  <td className="px-4 py-3 text-xs">
                    {u.is_premium ? <span className="text-purple-700 font-semibold">Premium</span> : <span className="text-gray-400">Free</span>}
                    {u.subscription_status && (
                      <div className="text-[10px] text-gray-400 mt-0.5 capitalize">{u.subscription_status.replace(/_/g, ' ')}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-700">
                    {u.paid_at ? <>{new Date(u.paid_at).toLocaleDateString()}</> : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-xs font-semibold">
                    {u.total_commission > 0 ? <span className="text-green-700">+{u.currency} {u.total_commission.toFixed(2)}</span>
                      : u.total_commission < 0 ? <span className="text-red-700">{u.currency} {u.total_commission.toFixed(2)}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-[11px] text-gray-500">
                    {new Date(u.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ConversionBadge({ value, refundCount }: { value: ReferredUser['conversion']; refundCount: number }) {
  const meta = {
    paid:        { label: 'Paid',        tint: 'bg-purple-50 text-purple-700' },
    trial:       { label: 'Trial',       tint: 'bg-amber-50 text-amber-800' },
    signup_only: { label: 'Signup only', tint: 'bg-gray-100 text-gray-600' },
    refunded:    { label: 'Refunded',    tint: 'bg-red-50 text-red-700' },
    none:        { label: '—',           tint: 'bg-gray-100 text-gray-400' },
  }[value] || { label: value, tint: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${meta.tint}`}>
      {meta.label}
      {refundCount > 0 && value !== 'refunded' && <span className="ml-1 text-red-700">· {refundCount} refund{refundCount === 1 ? '' : 's'}</span>}
    </span>
  );
}

